import React, { memo, useCallback, useMemo, useContext, useEffect, useState } from 'react';
import { Box, Drawer, Grid, Paper, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { NavLink, useHistory } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import ListIcon from '@mui/icons-material/List';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';

import SidebarMenu from '../SidebarMenu';
import SidebarMenuTree from './SidebarMenuTree';
// import SidebarFavoritesTree from './SidebarFavoritesTree';
// import SidebarHeader from '../SidebarHeader';
import { useIsMobile } from '../../../utils/useScreenSize';
import { exchangeRateToText } from '../../../routes/Setup/Finance/Currencies/useExchangeRates';
import { Context as ClientsContext } from '../../../api/clients/context';
import { Context as ExchangeRatesContext } from '../../../api/exchangeRates/context';
import { Context as CurrenciesContext } from '../../../api/currencies/context';
import { Context as UserPreferencesContext } from '../../../api/userPreferences/context';
import sidebarStyles from './sidebarStyles';
import { useSidebarResize } from './useSidebarResize';
import {
    HEADER_HEIGHT,
    SIDEBAR_WIDTH_VAR,
    SIDEBAR_COLLAPSED_WIDTH,
    SIDEBAR_MOTION,
    SIDEBAR_FADE,
    SIDEBAR_FADE_DURATION
} from '../layoutConstants';

const Sidebar = (props) => {
    const {
        sidebarToggle,
        setSidebarToggle,
        setSidebarCollapsed,
        sidebarResizing = false,
        onSidebarResizingChange
    } = props;
    const isMobile = useIsMobile();
    const history = useHistory();
    const { update: updatePreferences } = useContext(UserPreferencesContext);
    // Saved per user, so the sidebar opens at this width on any device.
    // `undefined` drops the key and returns to the company default.
    const commitSidebarWidth = useCallback(
        (width) => updatePreferences({ sidebarWidth: width ?? undefined }),
        [updatePreferences]
    );
    const { startDrag, resetWidth } = useSidebarResize({
        collapsed: sidebarToggle,
        setCollapsed: setSidebarCollapsed,
        onResizingChange: onSidebarResizingChange,
        onWidthCommit: commitSidebarWidth
    });

    const classes = sidebarStyles({ sidebarToggle });

    const clients = useContext(ClientsContext);
    const exchangeRatesContext = useContext(ExchangeRatesContext);
    const currenciesContext = useContext(CurrenciesContext);

    const [primaryCurrencyValue, setPrimaryCurrencyValue] = useState(null);
    //const [isSelected, setIsSelected] = useState('menu');

    const primaryCurrencyId = clients.selected?.currencySettings?.primaryCurrency;
    const localCurrencyId = clients.selected?.currencySettings?.localCurrency;
    // Syncs the primary currency, local currency and exchangeRates data with the value of the primary currency
    useEffect(() => {
        if (primaryCurrencyId && localCurrencyId && exchangeRatesContext?.todayData) {
            const suitableRates =
                exchangeRatesContext.todayData.find((rate) => {
                    return rate.base._id === primaryCurrencyId && rate.quote._id === localCurrencyId;
                }) || {};
            const suitableCurrency =
                currenciesContext.dataAsArray.find((currency) => {
                    return currency._id === primaryCurrencyId;
                }) || {};
            setPrimaryCurrencyValue({ ...suitableCurrency, value: suitableRates?.exchangeRate });
        } else {
            setPrimaryCurrencyValue(null);
        }
    }, [currenciesContext.dataAsArray, exchangeRatesContext.todayData, localCurrencyId, primaryCurrencyId]);

    const code = primaryCurrencyValue?.code;
    const value = primaryCurrencyValue?.value;
    const primaryCurrencyText =
        typeof value === 'number' && code ? `${code} = ${exchangeRateToText(value)}` : '';

    // The mobile drawer is either open or gone, so it never renders the rail.
    //
    // The content is laid out at its OWN width — full rail or icon rail — and
    // the Paper clips it. Let it inherit the animating width instead and every
    // frame of the slide re-wraps thirty labels into a narrower box, which is
    // what made the motion look like it was struggling. Now only the clip
    // moves; the content inside it never reflows.
    const renderMenuContent = (collapsed = false, width = '100%') => (
        <div
            style={{
                height: '100%',
                width,
                flexShrink: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                <SidebarMenuTree collapsed={collapsed} onExpand={setSidebarToggle} />
            </div>
            <div style={{ flexShrink: 0 }}>
                <Divider className={classes.divider} />
                {/* The band stays even when collapsed — its height is what keeps
                    the divider above it on the app footer's top border — but the
                    rate text has no room in the rail. */}
                <Grid container alignItems="center" justifyContent="center" className={classes.sidebarFooterBand}>
                    {!collapsed && (
                        <Typography variant="h5" color="inherit">
                            {primaryCurrencyText}
                        </Typography>
                    )}
                </Grid>
            </div>
        </div>
    );

    return isMobile ? (
        <Drawer
            anchor="left"
            open={sidebarToggle}
            onClose={setSidebarToggle}
            variant="temporary"
            elevation={4}
            PaperProps={{ style: { width: 260, maxWidth: '80vw' } }}
        >
            {renderMenuContent()}
        </Drawer>
    ) : (
        <Paper
            square
            elevation={11}
            style={{
                height: `calc(var(--app-vh, 100vh) - ${HEADER_HEIGHT}px)`,
                overflow: 'hidden',
                zIndex: 60,
                position: 'fixed',
                top: `${HEADER_HEIGHT}px`,
                left: 0,
                transition: sidebarResizing ? 'none' : `width ${SIDEBAR_MOTION}`,
                // Closed is a rail, not nothing: the section icons stay.
                width: sidebarToggle ? `${SIDEBAR_COLLAPSED_WIDTH}px` : SIDEBAR_WIDTH_VAR
            }}
        >
            {/* Both the full menu and the icon rail stay mounted, stacked, and
                cross-fade as the width moves. Swapping one for the other at the
                start of the motion read as a jump; keeping the full tree mounted
                also keeps its open sections across a fold. The hidden layer is
                `visibility: hidden` once faded, so it takes no clicks or focus. */}
            <div style={{ position: 'relative', height: '100%', backgroundColor: 'inherit' }}>
                {[false, true].map((railLayer) => {
                    const active = railLayer === sidebarToggle;
                    return (
                        <div
                            key={railLayer ? 'rail' : 'full'}
                            aria-hidden={!active}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                height: '100%',
                                backgroundColor: 'inherit',
                                opacity: active ? 1 : 0,
                                visibility: active ? 'visible' : 'hidden',
                                transition: `opacity ${SIDEBAR_FADE}, visibility 0s linear ${active ? '0s' : SIDEBAR_FADE_DURATION}`
                            }}
                        >
                            {renderMenuContent(railLayer, railLayer ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH_VAR)}
                        </div>
                    );
                })}
            </div>
            {/* The drag edge: resize, snap to the icon rail, double-click to reset. */}
            <Box
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize sidebar"
                title="Drag to resize · double-click to reset"
                onPointerDown={startDrag}
                onDoubleClick={resetWidth}
                sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '6px',
                    height: '100%',
                    cursor: 'col-resize',
                    zIndex: 1,
                    backgroundColor: sidebarResizing ? 'var(--nav-active-bg)' : 'transparent',
                    '&:hover': { backgroundColor: 'var(--nav-active-bg)' }
                }}
            />
        </Paper>
    );
};

Sidebar.propTypes = {
    sidebarToggle: PropTypes.bool.isRequired,
    setSidebarToggle: PropTypes.func.isRequired,
    setSidebarCollapsed: PropTypes.func.isRequired,
    sidebarResizing: PropTypes.bool,
    onSidebarResizingChange: PropTypes.func.isRequired
};

export default memo(Sidebar);
